// The Miracle Healer's tick: mend what is standing around her, or cook.
// The only thing in the game that puts a piece's health back — §27 only takes
// it away (knight repair is retired, §31 R4). See
// `docs/claude/armies.md`.

import {
  areEnemies,
  hurtAt,
  maxHealthOf,
  pieceAt,
  setHurt,
  variantAt,
  type Position
} from "../position";
import { pieceColor, type Color } from "../board";
import { RULES, variantHeals } from "../rules";
import { refinesAt } from "./machines";
import { liveSquares } from "./state";
import { neighbours } from "./structures";

/**
 * The hurt pieces a healer at `at` would mend: herself first, then her eight.
 *
 * `neighbours`' fixed order, so two clients replaying one log mend the same
 * set. `areEnemies` and never the side — a split army's wounded are hers too.
 */
export function healableAround(position: Position, at: number, owner: Color): number[] {
  const targets: number[] = [];
  for (const sq of [at, ...neighbours(at)]) {
    const piece = pieceAt(position, sq);
    if (!piece || areEnemies(position, owner, pieceColor(piece))) continue;
    if (hurtAt(position, sq) === 0) continue;
    // A last king's max health is nought. Dead code today — it is never hurt —
    // and asked anyway, for the reason the armour check in `attacks.ts` is.
    if (maxHealthOf(position, sq) === 0) continue;
    targets.push(sq);
  }
  return targets;
}

/**
 * Every healer of one side mends, and reports which were busy doing it.
 *
 * `runRefineries` skips the busy set, which is the whole of "she refines when
 * there is nobody to heal": one machine, one job a tick. Health only — armor
 * comes back by regeneration alone (§31 K5, `regen.ts`).
 */
export function runHealers(
  position: Position,
  owner: Color
): { healed: number; busy: ReadonlySet<number> } {
  let healed = 0;
  const busy = new Set<number>();
  if (position.variant.size === 0 || position.hurt.size === 0) return { healed, busy };

  for (const sq of liveSquares(position.board)) {
    const piece = pieceAt(position, sq);
    if (pieceColor(piece) !== owner || !variantHeals(variantAt(position, sq))) continue;
    // The set she is skipped from is the set she has to be in: mending is paid
    // for with the tick she would have cooked in, so a bishop that is
    // `NO_MACHINE` has nothing to spend and mends nothing.
    if (!refinesAt(position, sq)) continue;

    const targets = healableAround(position, sq, owner);
    if (targets.length === 0) continue;
    for (const target of targets) {
      // Clamped at whole, which `setHurt` writes as nought.
      const mended = Math.min(RULES.healRate, hurtAt(position, target));
      setHurt(position, target, hurtAt(position, target) - mended);
      healed += mended;
    }
    busy.add(sq);
  }
  return { healed, busy };
}
